import { useState, useEffect, useRef } from "react";

/**
 * Reactively tracks whether the browser has network connectivity.
 * Returns true when online, false when offline.
 *
 * The offline transition is DEBOUNCED: `navigator.onLine` briefly flaps to
 * offline during some Wi-Fi handovers (notably when the phone associates with a
 * GoPro's own Wi-Fi AP while 5G is still available), which would otherwise flash
 * a false "offline" banner for a second. We only flip to offline after it stays
 * offline for `OFFLINE_DEBOUNCE_MS`; coming back online is applied immediately.
 */
const OFFLINE_DEBOUNCE_MS = 3000;

export default function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );
  const offlineTimerRef = useRef(null);

  useEffect(() => {
    const clearOfflineTimer = () => {
      if (offlineTimerRef.current) {
        clearTimeout(offlineTimerRef.current);
        offlineTimerRef.current = null;
      }
    };

    const handleOnline = () => {
      clearOfflineTimer();
      setIsOnline(true);
    };

    const handleOffline = () => {
      // Debounce: wait to confirm the offline state is sustained (not a brief
      // Wi-Fi handover flap) before flipping the app into offline mode.
      clearOfflineTimer();
      offlineTimerRef.current = setTimeout(() => {
        offlineTimerRef.current = null;
        // Re-check in case connectivity returned without an 'online' event.
        if (typeof navigator === "undefined" || navigator.onLine === false) {
          setIsOnline(false);
        }
      }, OFFLINE_DEBOUNCE_MS);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      clearOfflineTimer();
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return isOnline;
}
